import styled from "@emotion/styled";
import {Box, Modal} from "@mui/material";
import {media} from "assets/css/media";
import {ReactComponent as IconClose} from "assets/img/icon/close.svg";
import {ReactComponent as FacebookIcon} from "assets/img/icon/facebook_2.svg";
import {ReactComponent as TwitterIcon} from "assets/img/icon/twitter.svg";
import {Bindings} from "data/constants/bindings";
import {useViewController} from "data/services/locator/locator_hook.service";
import {observer} from "mobx-react";
import React from "react";
import {NavLink} from "react-router-dom";
import {Exist} from "views/components/exist/exist.component";
import {LinkLabel} from "views/components/form";
import {
	CloseBtn,
	ModalBody,
	ModalBox,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalText,
	ModalTitle,
} from "views/components/modals/common/common.component";
import {Preloader} from "views/components/preloader";
import {DownloadButton, PrimaryButton, SocialShareButton} from "views/components/style";
import type {IShareController} from "views/controllers/share/share.controller";

const ShareImageBox = styled(Box)`
	width: 100%;

	img {
		margin: 0 auto;
		display: block;
		width: 100%;
	}
`;

const ButtonRow = styled(Box)`
	display: flex;
	justify-content: space-around;
	align-items: center;
	width: 100%;
	gap: 12px;

	button {
		flex: unset;
		width: 44px;
		padding: 0;
	}

	@media (max-width: ${media.mobile}) {
		flex-direction: column;
	}
`;

const ModalTextStyled = styled(ModalText)`
	margin-bottom: 0;
`;

const ButtonMobileRow = styled.div`
	display: flex;
	flex-direction: row;
	gap: 12px;
	width: 100%;

	@media (max-width: ${media.mobile}) {
		flex-direction: column;
	}
`;

const DownloadButtonDesktop = styled(DownloadButton)`
	flex-grow: 1;
`;

export const ModalShare: React.FC = observer(() => {
	const controller = useViewController<IShareController>(Bindings.ShareController);
	const {
		i18n,
		isOpenShareModal,
		closeShareModal,
		shareImagePath,
		isMobileShareVisible,
		shareViaFB,
		shareViaTW,
		shareViaMobile,
	} = controller;

	if (!isOpenShareModal) {
		return null;
	}

	const title = i18n.t("modal.share.title", "share your picks");
	const message = i18n.t(
		"modal.share.description",
		"Great your team(s) are in and you are in the draw to win X prize"
	);
	const terms = i18n.t("modal.share.terms", "View Terms and Conditions.");

	return (
		<Modal open={isOpenShareModal}>
			<ModalBox className="share_box">
				<ModalContent className="share_content">
					<CloseBtn onClick={closeShareModal}>
						<IconClose />
					</CloseBtn>
					<ModalHeader style={{flexDirection: "column"}}>
						<ModalTitle>{title}</ModalTitle>
						<ModalTextStyled>
							{message}{" "}
							<NavLink to={`help/terms`} target={"_blank"}>
								<LinkLabel>{terms}</LinkLabel>
							</NavLink>
						</ModalTextStyled>
					</ModalHeader>
					<ModalBody>
						<ShareImageBox>
							{shareImagePath.preview ? (
								<img src={shareImagePath.preview} alt="share" />
							) : (
								<Preloader />
							)}
						</ShareImageBox>
					</ModalBody>
					<ModalFooter>
						<Exist when={!isMobileShareVisible}>
							<ButtonRow>
								<DownloadButtonDesktop
									className={!shareImagePath.link ? "disabled" : ""}
									href={shareImagePath.link}
									download>
									{i18n.t("modal.saved.load_btn", "Download team(s)")}
								</DownloadButtonDesktop>
								<SocialShareButton
									variant="contained"
									onClick={shareViaFB}
									disabled={!shareImagePath.link}>
									<FacebookIcon />
								</SocialShareButton>
								<SocialShareButton
									variant="contained"
									onClick={shareViaTW}
									disabled={!shareImagePath.link}>
									<TwitterIcon />
								</SocialShareButton>
							</ButtonRow>
						</Exist>
						<Exist when={isMobileShareVisible}>
							<ButtonMobileRow>
								<DownloadButton
									className={!shareImagePath.link ? "disabled" : ""}
									href={shareImagePath.link}
									download>
									{i18n.t("modal.saved.load_btn", "Download team(s)")}
								</DownloadButton>
								<PrimaryButton
									disabled={!shareImagePath.link}
									fullWidth
									onClick={shareViaMobile}
									variant="contained"
									size="large">
									{i18n.t("modal.saved.share_btn", "Share")}
								</PrimaryButton>
							</ButtonMobileRow>
						</Exist>
					</ModalFooter>
				</ModalContent>
			</ModalBox>
		</Modal>
	);
});
